import React, { useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { FlexCol } from '../../../../shared/FlexCol';
import { Body, Label, Title } from '../../../../shared/styled-components/styled_text';
import { FlexRow } from '../../../../shared/FlexRow';
import { Tree } from './ApplicationPreview';

interface Props {
  tree: Tree;
}

export const TreeList: React.FC<Props> = ({ tree }) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <Container expanded={expanded}>
      <MediaContainer>
        <MediaWrapper>
          {tree.media.map((m, i) => (
            <button key={`media-${i}`}>
              <MediaIcon src={m.file?.path_full} alt={m.icon} />
            </button>
          ))}
        </MediaWrapper>
      </MediaContainer>

      <ContentWrapper open={expanded} hasContent={tree.posts.filter(p => p.is_activated).length > 0}>
        <FlexCol width={'100%'} align={'flex-start'} gap={'1.6rem'}>
          <Title size={'s'} style={{ color: 'var(--content-color-secondary)' }}>
            채용 중인 공고
          </Title>
          <div style={{ width: '100%', height: '1px', backgroundColor: 'var(--border-color)' }} />
          {tree.posts.map(p =>
            p.is_activated ? (
              <Job key={`post-container-${p.id}`}>
                <FlexCol width={'100%'} align={'flex-start'} gap={'0.4rem'}>
                  <div>
                    <Label size={'s'} style={{ cursor: 'pointer' }}>
                      {p.title}
                    </Label>
                    <Caption>
                      {p.team.name} | {p.part.name}
                    </Caption>
                  </div>
                  <FlexRow width={'100%'} justify={'flex-start'} gap={'0.6rem'}>
                    <img src={'/images/workspace-wizard/calendar_clock.svg'} alt={''} style={{ width: '1.8rem', height: '1.8rem' }} />
                    {p.end ? (
                      <>
                        <Body size={'s'} style={{ color: 'var(--content-color-tertiary)' }}>
                          {dayjs(p.end).format('~MM.DD')}
                        </Body>
                        {dayjs(p.end).isSame(dayjs(), 'day') ? (
                          <Tag style={{ color: 'white', backgroundColor: 'var(--tag-bg-filled-primary)' }}>오늘 마감</Tag>
                        ) : (
                          <Tag>D-{dayjs(p.end).diff(dayjs(), 'day') + 1}</Tag>
                        )}
                      </>
                    ) : (
                      <Body size={'s'} style={{ color: 'var(--content-color-tertiary)' }}>
                        상시채용
                      </Body>
                    )}
                  </FlexRow>
                </FlexCol>
              </Job>
            ) : null,
          )}
          {expanded && (
            <MoreButtonWrapper>
              <MoreButton open={expanded} onClick={() => setExpanded(!expanded)}>
                <Label size={'s'} style={{ color: 'var(--mono-40)' }}>
                  {expanded ? '접기' : '더보기'}
                </Label>
              </MoreButton>
            </MoreButtonWrapper>
          )}
        </FlexCol>
      </ContentWrapper>
      {!expanded && (
        <MoreButtonWrapper>
          <MoreButton open={expanded} onClick={() => setExpanded(!expanded)}>
            <Label size={'s'} style={{ color: 'var(--mono-40)' }}>
              {expanded ? '접기' : '더보기'}
            </Label>
          </MoreButton>
        </MoreButtonWrapper>
      )}
    </Container>
  );
};

const Container = styled.div<{ expanded?: boolean }>`
  width: 100%;
  border-radius: 6px;
  background: var(--background-2);
  padding: 4.2rem 2.4rem 4rem;
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  ${props => props.expanded && 'padding-bottom: 9rem;'}
  > * {
    width: 100%;
  }
`;

const Tag = styled.div`
  min-width: 0;
  height: 2.2rem;
  min-height: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  font-weight: 500;
  font-size: 1.4rem;
  padding: 0 1rem;
  border-radius: 1000px;
  box-sizing: border-box;
  cursor: pointer;
  user-select: none;
  max-width: max-content;
  flex-shrink: 0;
  color: #1851dd;
  background-color: var(--primary-op-08);

  > span {
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const MediaContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(0, -50%);
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const MediaWrapper = styled.div`
  width: fit-content;
  padding: 1rem 1.6rem;
  background-color: white;
  border-radius: 100px;
  box-shadow: 0 6px 16px 2px rgba(50, 52, 55, 0.12);
  display: flex;
  flex-direction: row;
  gap: 1.1rem;
`;

const MediaIcon = styled.img`
  width: 2.8rem;
  height: 2.8rem;
  object-fit: cover;
  background-repeat: no-repeat;
  flex-shrink: 0;
  border-radius: 50%;
  cursor: pointer;
`;

const ContentWrapper = styled.div<{ open?: boolean; hasContent?: boolean }>`
  width: 100%;
  height: fit-content;
  max-height: 37.3rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  ${props => props.open && 'max-height: unset;'}
  ${props => props.hasContent && 'min-height: 19rem;'}
`;

const Caption = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 160%;
  color: var(--content-color-tertiary);
`;

const MoreButtonWrapper = styled.div`
  width: 100%;
  padding: 1.2rem 2rem 2rem;
  background-color: white;
  border-top: 1px solid var(--background-2);
  position: absolute;
  left: 0;
  bottom: 0;
`;

const MoreButton = styled.button<{ open?: boolean }>`
  width: 100%;
  border: 1px solid var(--mono-40);
  border-radius: 8px;
  padding-block: 0.8rem;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;

  &:after {
    content: '';
    width: 1.8rem;
    height: 1.8rem;
    mask-image: url('/images/common/chevron.svg');
    background-color: var(--mono-40);
    background-size: cover;
    rotate: 90deg;
    ${props => !props.open && 'rotate: 270deg;'}
  }
`;

const Job = styled.div`
  width: 100%;
  height: fit-content;
  padding: 1.6rem;
  background-color: white;
  border-radius: 16px;
  box-sizing: border-box;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  cursor: pointer;
  color: black;
  text-decoration: none;
`;
